import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, filter, finalize, forkJoin, map, merge, Observable, OperatorFunction, Subject, switchMap, take } from 'rxjs';
import { Contact } from '../../models/contact/contact';
import { JobContactStatistic } from '../../models/statistics/job-contact-statistic';
import { JobYearStatistic } from '../../models/statistics/job-year-statistic';
import { ContactService } from '../../services/contact.service';
import { StatisticsService } from '../../services/statistics.service';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.css'
})
export class StatisticsComponent implements OnInit {
  private readonly contactService = inject(ContactService);
  private readonly statisticsService = inject(StatisticsService);
  
  selectedContact: Contact;
  selectedYear: number;
  selectedMonth: number;
  contacts: Contact[];
  yearStatistics: JobYearStatistic[] = [];
  contactStatistics: JobContactStatistic[] = [];
  years: number[];
  yearTotal: number;
  loading: boolean = true;
  months: string[] = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];


  ngOnInit(): void {
    this.selectedYear = new Date().getFullYear();
    this.selectedMonth = new Date().getMonth();

    forkJoin(
      [this.loadContacts(), 
        this.loadContactStatistics(this.selectedYear, +this.selectedMonth + 1, null),
        this.loadYearStatistics(this.selectedYear, null),
        this.loadJobYears()
      ])
    .pipe(
      take(1),
      finalize(() => { this.loading = false; })
    )
    .subscribe(([contacts, contactStatistics, yearStatistics, years]) => {
      //contacts
      this.contacts = contacts.list;
      this.contacts.unshift({id: -1, name: 'Alle contacten', type: null, vatNumber: null, addresses: null, details: null});
      this.selectedContact = this.contacts[0];
  
      // years
      this.years = years;

      // contact statistics
      this.contactStatistics = contactStatistics;
      this.contactStatistics = contactStatistics.sort((a, b) => 
        a.contact.name.toLowerCase().localeCompare(b.contact.name.toLowerCase())
      );

      // year statistics
      this.yearStatistics = yearStatistics;
      this.months.forEach((month, index) => {
        var results = yearStatistics.filter(x => x.month === index + 1);
        if (results.length == 0) {
          this.yearStatistics.push({year: this.selectedYear, month: index + 1, count: 0});
        }
      });
      this.yearStatistics = this.yearStatistics.sort((a, b) => a.month - b.month);
      this.yearTotal = this.yearStatistics.reduce((acc, stat) => acc + stat.count, 0);

      
    })    
  }

  onDateChange() {
    this.loadData();
  }

  onContactChange($event: any) {
    this.selectedContact = $event.item;
    this.loadData();
  }

  loadData() {
    var yearStatistics$: Observable<JobYearStatistic[]> = this.loadYearStatistics(this.selectedYear, this.selectedContact.id);
    var contactStatistics$: Observable<JobContactStatistic[]> = this.loadContactStatistics(this.selectedYear, +this.selectedMonth + 1, this.selectedContact.id);

    forkJoin([yearStatistics$, contactStatistics$])
    .pipe(take(1))
    .subscribe(([yearStatistics, contactStatistics]) => {
      this.yearStatistics = yearStatistics;
      this.months.forEach((month, index) => {
        var results = yearStatistics.filter(x => x.month === index + 1);
        if (results.length == 0) {
          this.yearStatistics.push({year: this.selectedYear, month: index + 1, count: 0});
        }
      });
      this.yearStatistics = this.yearStatistics.sort((a, b) => a.month - b.month);
      this.yearTotal = this.yearStatistics.reduce((acc, stat) => acc + stat.count, 0);

      this.contactStatistics = contactStatistics.sort((a, b) => 
        a.contact.name.toLowerCase().localeCompare(b.contact.name.toLowerCase())
      );

    });
  }

  loadContacts() {
    return this.contactService.getContacts();
  }

  loadJobYears() {
    return this.statisticsService.getJobYears();
  }

  loadYearStatistics(year: number, contactId: number|null) {
    if (contactId === -1) {
      contactId = null;
    }
    return this.statisticsService.getJobYearStatistics(year, contactId);
  }

  loadContactStatistics(year: number, month: number, contactId: number|null) {
    if (contactId === -1) {
      contactId = null;
    }
    return this.statisticsService.getJobContactStatistics(year, month, contactId);
  }

  @ViewChild('contactInstance') contactInstance: NgbTypeahead;
  contactFocus$ = new Subject<string>();
  contactClick$ = new Subject<string>();

  search: OperatorFunction<string, readonly Contact[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.contactClick$.pipe(filter(() => !this.contactInstance.isPopupOpen()));
    const inputFocus$ = this.contactFocus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$)
    .pipe(
      switchMap(term => term === '' 
        ? this.contactService.getFilteredContacts(
        {
          offset: null,
          amount: null,
          name: null,
          searchAll: null,
        })
        .pipe(
          map(x => {
              x.list.unshift({id: -1, name: 'Alle contacten', type: null, vatNumber: null, addresses: null, details: null});
            return x.list
          })
        )
        : 
        this.contactService.getFilteredContacts(
        {
          offset: null,
          amount: null,
          name: term,
          searchAll: null,
        })
        .pipe(
          map(x => {
              x.list.unshift({id: -1, name: 'Alle contacten', type: null, vatNumber: null, addresses: null, details: null});
            return x.list
          })
        )
      )
    )
  }

  formatter = (x: Contact) => x.name;

  onContactClick(event: Event) {
    this.selectedContact = null;
    this.contactClick$.next((event.target as HTMLInputElement).value);
  }

  onContactFocus(event: Event) {
    this.selectedContact = null;
    this.contactFocus$.next((event.target as HTMLInputElement).value);  
  }
}
